"use client";

import { RegistrationError } from "@/lib/app-error";
import { callOperation as op } from "@/lib/f360-client";
import type { AppErrorCode } from "@/lib/error-config";

/* Post-login facility data against facility_setup_v3 (cookie session).
   All calls are scoped server-side to the administrator's own facilities.
   Every call goes through the F360 operation route (lib/f360-client.ts →
   POST /api/f360/<operation>); the Frappe method names live server-side in
   lib/server/operations.ts and the route already unwraps the v3 envelope. */

export type SetupChecklist = {
  hasContacts: boolean;
  contactsCount: number;
  hasBanks: boolean;
  banksCount: number;
  isComplete: boolean;
};

export type LiveFacility = {
  name: string;
  facilityName: string;
  hieId: string;
  registrationNumber: string;
  facilityCode: string;
  facilityType: string;
  county: string;
  subCounty: string;
  operationalStatus: string;
  isOnboarded?: boolean;
  onboardingComplete?: boolean;
  setupChecklist?: SetupChecklist;
};

export type FacilityDetails = {
  facility: Record<string, string>;
  /** Child tables on the record (bed capacity, services, admission types…), as plain rows. */
  tables: Record<string, Record<string, string>[]>;
  contacts: { contact_name: string; phone_number: string }[];
  banks: { bank_name: string; branch_name: string; account_name: string; account_number: string }[];
  affiliationCounts: { pending: number; active: number };
  onboardingComplete?: boolean;
  setupChecklist?: SetupChecklist;
};

export type OnboardingStatus = {
  hasPendingOnboarding: boolean;
  pendingCount: number;
  completedCount: number;
  total: number;
  nextFacility: LiveFacility | null;
  resumePath: string | null;
};

export type Affiliation = {
  affiliationId: string;
  status: string;
  role: string;
  employmentType: string;
  designation: string;
  requestedDate: string;
  expiryDate: string;
  /** Still awaiting the invitee and under the resend limit. */
  canResend?: boolean;
  /** "Staff" for non-clinical staff; practitioner rows otherwise. */
  affiliationType?: string;
  practitioner: { name: string; fullName: string; registrationNumber: string; phone: string; email: string };
};

export type BulkRowResult = {
  identificationNumber: string;
  ok: boolean;
  error?: string;
  affiliationId?: string;
  practitioner?: Affiliation["practitioner"];
};

export type AffiliationDetails = {
  affiliation: Record<string, string>;
  practitioner: Record<string, string>;
  facility: Record<string, string>;
};

type Envelope = { status?: string; message?: unknown; data?: unknown };

export function toError(error: unknown): RegistrationError {
  if (error instanceof RegistrationError) {
    if (error.code !== "UNKNOWN") return error;
    const detail = error.detail ?? serverDetail(error);
    return new RegistrationError(codeFor(detail, error.status), error.status, detail);
  }
  const status = error && typeof error === "object" && ("httpStatus" in error || "status" in error) ? Number(((error as { httpStatus?: unknown }).httpStatus ?? (error as { status?: unknown }).status)) || 0 : 0;
  const detail = serverDetail(error);
  return new RegistrationError(codeFor(detail, status), status, detail);
}

/** Pull a human-readable message out of an unexpected throw, if any.
    Operation-route failures already carry `detail` (e.g. "Region 'SIRISIA'
    is not set up yet..."); this covers anything else so screens still get
    safe copy. Mirrors registration-service. */
function serverDetail(error: unknown): string | undefined {
  if (!error || typeof error !== "object") return undefined;
  const bag = error as Record<string, unknown>;
  // frappe SDK nests the response body under response.data.
  try {
    const nested = (bag.response as { data?: unknown })?.data as Envelope | undefined;
    const inner = nested?.message && typeof nested.message === "object" ? (nested.message as Envelope) : nested;
    if (inner && typeof inner.message === "string" && inner.message.trim()) return inner.message.trim();
  } catch { /* ignore */ }
  for (const key of ["message", "error", "data"]) {
    const value = bag[key];
    if (typeof value === "string" && value.trim() && value !== "Forbidden" && !/^\d+$/.test(value.trim()) && value.length < 200) {
      return value;
    }
  }
  return undefined;
}

function codeFor(detail: string | undefined, status: number): AppErrorCode {
  const haystack = (detail ?? "").toLowerCase();
  // Route by message first: the backend sometimes answers with a generic
  // HTTP status, so the message is the reliable signal.
  if (haystack.includes("sign in")) return "SESSION_EXPIRED";
  if (haystack.includes("not linked") || haystack.includes("no facilities are linked")) return "ACCESS_DENIED";
  if (haystack.includes("already has an active")) return "FACILITY_ALREADY_ONBOARDED";
  if (haystack.includes("no practitioner record")) return "REGISTRY_NOT_FOUND";
  if (haystack.includes("not set up yet") || haystack.includes("not set up")) return "SETUP_BLOCKED";
  const code: AppErrorCode = status === 0 ? "NETWORK_ERROR" : status === 401 ? "SESSION_EXPIRED" : status === 403 ? "ACCESS_DENIED" : status === 404 ? "NOT_FOUND" : status === 429 ? "RATE_LIMITED" : status >= 500 ? "INTERNAL_SERVER_ERROR" : "UNKNOWN";
  return code;
}

const text = (value: unknown) => (value === null || value === undefined ? "" : String(value).trim());

const toChecklist = (value: unknown): SetupChecklist | undefined => {
  if (!value || typeof value !== "object") return undefined;
  const row = value as Record<string, unknown>;
  const contactsCount = Number(row.contacts_count) || 0;
  const banksCount = Number(row.banks_count) || 0;
  const hasContacts = row.has_contacts === true || contactsCount > 0;
  const hasBanks = row.has_banks === true || banksCount > 0;
  return {
    hasContacts,
    contactsCount,
    hasBanks,
    banksCount,
    isComplete: row.is_complete === true,
  };
};

const toLiveFacility = (row: Record<string, unknown>): LiveFacility => ({
  name: text(row.name),
  facilityName: text(row.facility_name),
  hieId: text(row.hie_id),
  registrationNumber: text(row.registration_number),
  facilityCode: text(row.facility_code),
  facilityType: text(row.facility_type),
  county: text(row.county),
  subCounty: text(row.sub_county),
  operationalStatus: text(row.operational_status),
  onboardingComplete: row.onboarding_complete === true,
  setupChecklist: toChecklist(row.setup_checklist),
});

/** Scalar fields as text; child tables (arrays of rows) kept apart as rows of text. */
function splitRecord(source: Record<string, unknown> | undefined) {
  const facility: Record<string, string> = {};
  const tables: Record<string, Record<string, string>[]> = {};
  for (const [key, value] of Object.entries(source ?? {})) {
    if (Array.isArray(value)) {
      tables[key] = value.filter((row): row is Record<string, unknown> => Boolean(row) && typeof row === "object")
        .map((row) => Object.fromEntries(Object.entries(row).map(([field, cell]) => [field, text(cell)])));
    } else if (value === null || typeof value !== "object") {
      facility[key] = text(value);
    }
  }
  return { facility, tables };
}

export function createFacilityService() {
  return {
    /** Facilities this administrator manages (onboarded one at a time). */
    async myFacilities(): Promise<LiveFacility[]> {
      try {
        const data = await op<{ facilities?: Record<string, unknown>[] }>("facilities.mine");
        return (data.facilities ?? []).map(toLiveFacility);
      } catch (error) { throw toError(error); }
    },

    /** Onboarding gate: which facilities still need stepper step 5.
        Used right after sign-in to decide between /sign-up/setup and the
        requested destination. Never throws - returns null when the backend
        does not answer (e.g. pre-migrate), so callers fall through. */
    async onboardingStatus(): Promise<OnboardingStatus | null> {
      try {
        const data = await op<{
          has_pending_onboarding?: unknown; pending_count?: unknown; completed_count?: unknown;
          total?: unknown; next_facility?: Record<string, unknown> | null; resume_path?: unknown;
        }>("facilities.onboarding-status");
        const next = data.next_facility && typeof data.next_facility === "object" ? toLiveFacility(data.next_facility) : null;
        return {
          hasPendingOnboarding: data.has_pending_onboarding === true,
          pendingCount: Number(data.pending_count) || 0,
          completedCount: Number(data.completed_count) || 0,
          total: Number(data.total) || 0,
          nextFacility: next,
          resumePath: typeof data.resume_path === "string" && data.resume_path ? data.resume_path : null,
        };
      } catch {
        return null;
      }
    },

    /** Finish stepper step 5 → Done. Requires at least one contact;
        bank accounts may follow later from the dashboard. */
    async completeOnboarding(facilityId: string): Promise<{ onboardingComplete: boolean; setupChecklist?: SetupChecklist }> {
      try {
        const data = await op<{ onboarding_complete?: unknown; setup_checklist?: unknown }>("facilities.complete-onboarding", { facility_id: facilityId });
        return {
          onboardingComplete: data.onboarding_complete === true,
          setupChecklist: toChecklist(data.setup_checklist),
        };
      } catch (error) { throw toError(error); }
    },

    /** Find a facility by registration number (returned in `available`), or link one by its FR code. */
    async linkAdditionalFacility(facilityId?: string, registrationNumber?: string): Promise<{
      available: LiveFacility[]; linked?: { facilityName: string; registrationNumber: string; hieId: string; affiliationId: string };
    }> {
      try {
        const data = await op<{
          facilities?: Record<string, unknown>[]; available_facilities?: Record<string, unknown>[];
          facility?: Record<string, unknown>; affiliation_id?: unknown;
        }>("facilities.link", facilityId ? { facility_id: facilityId } : registrationNumber ? { registration_number: registrationNumber } : {});
        // Prefer the full list (with is_onboarded flags); fall back to available-only.
        const source = (data.facilities ?? data.available_facilities ?? []) as Record<string, unknown>[];
        const available = source.map((row) => ({
          name: text(row.facility_fid),
          facilityName: text(row.facility_name),
          hieId: text(row.facility_fid),
          registrationNumber: text(row.registration_number),
          facilityCode: text(row.facility_code),
          facilityType: text(row.facility_type),
          county: text(row.county),
          subCounty: text(row.sub_county),
          operationalStatus: text(row.operational_status),
          isOnboarded: row.is_onboarded === true,
        }));
        const facility = data.facility;
        return {
          available,
          linked: facility ? {
            facilityName: text(facility.facility_name),
            registrationNumber: text(facility.registration_number),
            hieId: text(facility.hie_id),
            affiliationId: text(data.affiliation_id),
          } : undefined,
        };
      } catch (error) { throw toError(error); }
    },

    /** Practitioner roles from the Designation master. */
    async designations(): Promise<string[]> {
      try {
        const data = await op<unknown>("practitioners.designations");
        const list = Array.isArray(data) ? data : [];
        return list.map(text).filter(Boolean);
      } catch (error) { throw toError(error); }
    },

    /** Look up a practitioner on HWR + designations for their licensing body. */
    async lookupPractitioner(identificationType: string, identificationNumber: string): Promise<{
      practitioner: { fullName: string; registrationNumber: string; licensingBody: string; specialty: string; cadre: string; status: string; phone: string };
      designations: string[];
      regulatoryBody: string;
    }> {
      try {
        const data = await op<{
          practitioner?: Record<string, unknown>; designations?: unknown[]; regulatory_body?: unknown;
        }>("practitioners.lookup", {
          identification_type: identificationType, identification_number: identificationNumber,
        });
        const practitioner = data.practitioner ?? {};
        return {
          practitioner: {
            fullName: text(practitioner.full_name),
            registrationNumber: text(practitioner.registration_number),
            licensingBody: text(practitioner.licensing_body),
            specialty: text(practitioner.specialty),
            cadre: text(practitioner.cadre),
            status: text(practitioner.status),
            phone: text(practitioner.phone),
          },
          designations: Array.isArray(data.designations) ? data.designations.map(text).filter(Boolean) : [],
          regulatoryBody: text(data.regulatory_body),
        };
      } catch (error) { throw toError(error); }
    },

    async facilityDetails(facilityId: string): Promise<FacilityDetails> {
      try {
        const data = await op<{
          facility?: Record<string, unknown>; contacts?: Record<string, unknown>[];
          banks?: Record<string, unknown>[]; affiliation_counts?: Record<string, unknown>;
          onboarding_complete?: unknown; setup_checklist?: unknown;
        }>("facilities.details", { facility_id: facilityId });
        const { facility, tables } = splitRecord(data.facility);
        return {
          facility,
          tables,
          contacts: (data.contacts ?? []).map((c) => ({ contact_name: text(c.contact_name), phone_number: text(c.phone_number) })),
          banks: (data.banks ?? []).map((b) => ({ bank_name: text(b.bank_name), branch_name: text(b.branch_name), account_name: text(b.account_name), account_number: text(b.account_number) })),
          affiliationCounts: {
            pending: Number(data.affiliation_counts?.pending) || 0,
            active: Number(data.affiliation_counts?.active) || 0,
          },
          onboardingComplete: data.onboarding_complete === true,
          setupChecklist: toChecklist(data.setup_checklist),
        };
      } catch (error) { throw toError(error); }
    },

    /** Full registry (HFR) record for one of the admin's facilities. Null when
        the registry cannot be reached; My facility then shows the local record. */
    async facilityRegistryRecord(facilityId: string): Promise<Pick<FacilityDetails, "facility" | "tables"> | null> {
      try {
        const data = await op<{ facility?: Record<string, unknown> }>("facilities.registry-record", { facility_id: facilityId });
        return data.facility ? splitRecord(data.facility) : null;
      } catch {
        return null;
      }
    },

    async updateFacility(args: {
      facilityId: string;
      details: Record<string, string | number | null>;
      contacts: { contact_name: string; phone_number: string }[];
      banks: { bank_name: string; branch_name: string; account_name: string; account_number: string }[];
    }): Promise<void> {
      try {
        await op("facilities.update", { facility_id: args.facilityId, details: args.details, contacts: args.contacts, banks: args.banks });
      } catch (error) { throw toError(error); }
    },

    /** The signed-in admin's own affiliation to this facility: the oldest row
        that is theirs. Onboarding creates it with requested_by = the admin, but
        the list does not return that, so it is matched instead: a Staff row
        (non-practitioner admin, no Health Professional linked, so practitioner
        is empty) or a row whose practitioner email is the admin's.
        Rows come newest first; the admin's is on the last page. */
    async myFacilityAffiliation(facilityId: string, email: string): Promise<{ status: string; isPractitioner: boolean } | null> {
      const pageSize = 100;
      const first = await this.listAffiliations(facilityId, { page: 1, pageSize });
      const lastPage = Math.max(1, Math.ceil(first.total / pageSize));
      const rows = lastPage > 1 ? (await this.listAffiliations(facilityId, { page: lastPage, pageSize })).affiliations : first.affiliations;
      const me = email.trim().toLowerCase();
      const own = [...rows].reverse().find(({ practitioner }) => !practitioner.name || (me !== "" && practitioner.email.trim().toLowerCase() === me));
      return own ? { status: own.status, isPractitioner: Boolean(own.practitioner.name) } : null;
    },

    async listAffiliations(facilityId: string, opts?: { status?: string; search?: string; page?: number; pageSize?: number }): Promise<{ affiliations: Affiliation[]; total: number }> {
      try {
        const data = await op<{
          affiliations?: Record<string, unknown>[]; pagination?: Record<string, unknown>;
        }>("affiliations.list", {
          facility_id: facilityId,
          status: opts?.status || undefined,
          search: opts?.search || undefined,
          page: opts?.page ?? 1,
          page_size: opts?.pageSize ?? 20,
        });
        return {
          affiliations: (data.affiliations ?? []).map((row) => {
            const practitioner = (row.practitioner ?? {}) as Record<string, unknown>;
            return {
              affiliationId: text(row.affiliation_id),
              status: text(row.status),
              role: text(row.role),
              employmentType: text(row.employment_type),
              designation: text(row.designation),
              requestedDate: text(row.requested_date),
              expiryDate: text(row.expiry_date),
              canResend: row.can_resend === true || row.can_resend === 1,
              affiliationType: text(row.affiliation_type),
              practitioner: {
                name: text(practitioner.name),
                fullName: text(practitioner.full_name),
                registrationNumber: text(practitioner.registration_number),
                phone: text(practitioner.phone),
                email: text(practitioner.email),
              },
            };
          }),
          total: Number(data.pagination?.total_count) || 0,
        };
      } catch (error) { throw toError(error); }
    },

    /** Resend an invite the invitee did not get: a new code, emailed (optionally to a corrected address). */
    async resendAffiliation(affiliationId: string, newEmail?: string): Promise<{ maskedEmail: string; resendsRemaining: number }> {
      try {
        const data = await op<{ masked_email?: unknown; resends_remaining?: unknown }>("affiliations.resend", { affiliation_id: affiliationId, new_email: newEmail || undefined });
        return { maskedEmail: text(data.masked_email), resendsRemaining: Number(data.resends_remaining ?? 0) };
      } catch (error) { throw toError(error); }
    },

    async affiliationDetails(affiliationId: string): Promise<AffiliationDetails> {
      try {
        const data = await op<{
          affiliation?: Record<string, unknown>; practitioner?: Record<string, unknown>; facility?: Record<string, unknown>;
        }>("affiliations.details", { affiliation_id: affiliationId });
        const strMap = (record?: Record<string, unknown>) => {
          const out: Record<string, string> = {};
          for (const [key, value] of Object.entries(record ?? {})) out[key] = text(value);
          return out;
        };
        return {
          affiliation: strMap(data.affiliation),
          practitioner: strMap(data.practitioner),
          facility: strMap(data.facility),
        };
      } catch (error) { throw toError(error); }
    },

    /** Confirm a non-clinical staff member on the Client Registry (name + masked email only). */
    async lookupStaff(identificationType: string, identificationNumber: string): Promise<{ fullName: string; maskedEmail: string }> {
      try {
        const data = await op<{ full_name?: unknown; masked_email?: unknown }>("staff.lookup", {
          identification_type: identificationType, identification_number: identificationNumber,
        });
        return { fullName: text(data.full_name), maskedEmail: text(data.masked_email) };
      } catch (error) { throw toError(error); }
    },

    /** Pending Staff affiliation; the invite code goes to staffEmail (or the registry email). */
    async requestStaffAffiliation(args: {
      facilityId: string; identificationType: string; identificationNumber: string;
      jobTitle: string; employmentType: string; startDate: string; endDate?: string; staffEmail?: string;
    }): Promise<{ affiliationId: string; fullName: string; maskedEmail: string }> {
      try {
        const data = await op<{ affiliation_id?: unknown; full_name?: unknown; masked_email?: unknown }>("affiliations.request-staff", {
          facility_id: args.facilityId,
          identification_type: args.identificationType,
          identification_number: args.identificationNumber,
          job_title: args.jobTitle,
          employment_type: args.employmentType,
          start_date: args.startDate || undefined,
          end_date: args.endDate || undefined,
          staff_email: args.staffEmail || undefined,
        });
        return { affiliationId: text(data.affiliation_id), fullName: text(data.full_name), maskedEmail: text(data.masked_email) };
      } catch (error) { throw toError(error); }
    },

    async requestAffiliation(args: {
      facilityId: string; identificationType: string; identificationNumber: string;
      role?: string; employmentType?: string; designation?: string; endDate?: string;
    }): Promise<{ affiliationId: string; practitioner: Affiliation["practitioner"] }> {
      try {
        const data = await op<{ affiliation_id?: unknown; practitioner?: Record<string, unknown> }>("affiliations.request", {
          facility_id: args.facilityId,
          identification_type: args.identificationType,
          identification_number: args.identificationNumber,
          role: args.role || "Staff",
          employment_type: args.employmentType || "Full-time Employee",
          designation: args.designation || undefined,
          end_date: args.endDate || undefined,
        });
        const practitioner = data.practitioner ?? {};
        return {
          affiliationId: text(data.affiliation_id),
          practitioner: {
            name: text(practitioner.name),
            fullName: text(practitioner.full_name),
            registrationNumber: text(practitioner.registration_number),
            phone: text(practitioner.phone),
            email: text(practitioner.email),
          },
        };
      } catch (error) { throw toError(error); }
    },

    async requestAffiliationsBulk(facilityId: string, practitioners: Record<string, string>[]): Promise<{ sent: number; total: number; results: BulkRowResult[] }> {
      try {
        const data = await op<{ sent?: unknown; total?: unknown; results?: Record<string, unknown>[] }>("affiliations.request-bulk", { facility_id: facilityId, practitioners });
        return {
          sent: Number(data.sent) || 0,
          total: Number(data.total) || 0,
          results: (data.results ?? []).map((row) => ({
            identificationNumber: text(row.identification_number),
            ok: row.ok === true,
            error: text(row.error) || undefined,
            affiliationId: text(row.affiliation_id) || undefined,
            practitioner: row.practitioner as BulkRowResult["practitioner"],
          })),
        };
      } catch (error) { throw toError(error); }
    },

    /** Signed-in person's display name, role and affiliation state (`me.profile`).
        Proposed operation (F7, F17): returns null until healthpro_erp ships it,
        and callers fall back to the email-derived name and the administrator role. */
    async myProfile(): Promise<UserProfile | null> {
      try {
        const data = await op<Record<string, unknown>>("me.profile");
        const fullName = text(data.full_name);
        const role = text(data.role).toLowerCase();
        const affiliation = text(data.affiliation_status).toLowerCase();
        const practitioner = data.practitioner && typeof data.practitioner === "object" ? data.practitioner as Record<string, unknown> : null;
        return {
          fullName,
          firstName: text(data.first_name) || fullName.split(/\s+/)[0] || "",
          role: role === "professional" ? "professional" : "administrator",
          affiliation: affiliation === "accepted" || affiliation === "pending" ? affiliation : "none",
          details: {
            email: text(data.email),
            emailVerified: typeof data.email_verified === "boolean" ? data.email_verified : null,
            phone: text(data.phone_number),
            idType: text(data.identification_type),
            idNumber: text(data.identification_number),
            gender: text(data.gender),
            dateOfBirth: text(data.date_of_birth),
            verification: text(data.account_verification_status),
            organization: text(data.organization_name),
            region: text(data.organization_region_name),
            facilitiesCount: Number(data.facilities_count) || 0,
            lastLogin: text(data.last_login),
            practitioner: practitioner ? {
              registrationNumber: text(practitioner.registration_number),
              licensingBody: text(practitioner.licensing_body),
              cadre: text(practitioner.cadre),
              specialty: text(practitioner.specialty),
            } : null,
          },
        };
      } catch {
        return null;
      }
    },

    /** Whether the signed-in health worker has affiliation requests waiting
        (`affiliations.my-pending`). `registered: false` when the account has
        no Health Professional record; null when the backend did not answer. */
    async myPendingAffiliations(): Promise<{ registered: boolean; pending: boolean } | null> {
      try {
        const data = await op<unknown>("affiliations.my-pending");
        const empty = data === null || data === undefined || data === "" || (Array.isArray(data) && data.length === 0)
          || (typeof data === "object" && !Array.isArray(data) && Object.keys(data as object).length === 0);
        return { registered: true, pending: !empty };
      } catch (error) {
        const status = error instanceof RegistrationError ? error.status : 0;
        return status === 404 ? { registered: false, pending: false } : null;
      }
    },

    /** Affiliation counts by status for the dashboard strip (F12): one
        one-row list call per status, read from the list's own total
        (pagination.total_count). There is no separate counts endpoint. */
    async affiliationCounts(facilityId: string, statuses: readonly string[]): Promise<Record<string, number>> {
      const totals = await Promise.all(statuses.map((status) =>
        this.listAffiliations(facilityId, { status, page: 1, pageSize: 1 }).then((result) => result.total).catch(() => NaN),
      ));
      return Object.fromEntries(statuses.flatMap((status, index) => Number.isNaN(totals[index]) ? [] : [[status, totals[index]]]));
    },
  };
}

export type UserProfile = {
  fullName: string;
  firstName: string;
  role: "administrator" | "professional";
  affiliation: "none" | "pending" | "accepted";
  /** Everything else get_my_profile_v3 returns, for the My profile page. */
  details: ProfileDetails;
};

export type ProfileDetails = {
  email: string;
  /** null for accounts without an administrator record (not tracked). */
  emailVerified: boolean | null;
  phone: string;
  idType: string;
  /** Masked by the backend (last three digits). */
  idNumber: string;
  gender: string;
  dateOfBirth: string;
  verification: string;
  organization: string;
  region: string;
  facilitiesCount: number;
  lastLogin: string;
  practitioner: { registrationNumber: string; licensingBody: string; cadre: string; specialty: string } | null;
};

export type FacilityService = ReturnType<typeof createFacilityService>;

// Stateless: one instance, so effects keyed on it never re-run needlessly.
const facilityService = createFacilityService();

export function useFacilityService(): FacilityService {
  return facilityService;
}
